import React, { useMemo, useState } from "react";
import { FiRefreshCw, FiUpload } from "react-icons/fi";
import { useLocation } from "react-router-dom";
import CommonCard from "../../../../global/card";
import CommonSkeletonLoader from "../../../../global/loading";
import CommonPagination from "../../../../global/pagination";
import SOURCES from "../../../../../utils/constants";
import { QUERY_KEYS } from "../../../../../lib/query-constants";
import { useGenericQuery } from "../../../../../queries/use-generic-query";
import useDebounce from "../../../../../utils/customHooks";
import EnhancedStaticResidentialIpsList from "./enhancedStaticResidentialIpsList";
import EnhancedAddStaticResidentialIp from "./enhancedAddStaticResidentialIp";
import EnhancedBulkUploadStaticResidentialIp from "./enhancedBulkUploadStaticResidentialIp";
import EnhancedBulkHealthCheckStaticResidentialIp from "./enhancedBulkHealthCheckStaticResidentialIp";

const STATUS_OPTIONS = [
  { label: "All Statuses", value: "" },
  { label: "Unassigned", value: "unassigned" },
  { label: "Active", value: "active" },
  { label: "Flagged", value: "flagged" },
  { label: "Suspended", value: "suspended" },
];

const EnhancedStaticResidentialIps = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const pageFromQuery = parseInt(searchParams.get("page") || "1", 10);
  const limitFromQuery = parseInt(
    searchParams.get("limit") || SOURCES.DEFAULT_RECORDS_LISTING,
    10
  );
  const offsetFromQuery = parseInt(searchParams.get("offset") || "0", 10);

  const [viewType, setViewType] = useState(
    () => localStorage.getItem("staticResidentialIpsViewType") || SOURCES.DEFAULT_VIEW_TYPE
  );
  const [pagination, setPagination] = useState({
    offset: offsetFromQuery,
    limit: limitFromQuery,
    page: pageFromQuery,
  });
  const [statusFilter, setStatusFilter] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const [showAssignForm, setShowAssignForm] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [showBulkHealthCheck, setShowBulkHealthCheck] = useState(false);

  const debouncedSearch = useDebounce(searchInput, 500);

  const queryParams = useMemo(() => {
    const params = {
      offset: pagination.offset,
      limit: pagination.limit,
    };
    if (statusFilter) params.status = statusFilter;
    const q = debouncedSearch.trim();
    if (q) params.search = q;
    return params;
  }, [pagination.offset, pagination.limit, statusFilter, debouncedSearch]);

  const {
    data,
    isLoading,
    isFetching,
    refetch,
  } = useGenericQuery({
    endpoint: "admin/proxies",
    queryKey: QUERY_KEYS.staticResidentialIps.lists(queryParams),
    queryParams,
    onError: () => {},
    onSuccess: () => {},
  });

  const staticResidentialIps = Array.isArray(data?.results)
    ? data.results
    : Array.isArray(data)
      ? data
      : [];
  const totalRecords = data?.pagination?.totalRecords ?? data?.totalRecords ?? staticResidentialIps.length;

  const resetPagination = () => {
    setPagination((current) =>
      current.page === 1 && current.offset === 0
        ? current
        : { ...current, page: 1, offset: 0 }
    );
  };

  const handleSearchChange = (value) => {
    setSearchInput(value);
    resetPagination();
  };

  const handleStatusChange = (value) => {
    setStatusFilter(value || "");
    resetPagination();
  };

  return (
    <>
      <CommonCard
        isSearch={{
          show: true,
          searchInput,
          placeholder: "Search host, user email, platform...",
          setSearchInput: handleSearchChange,
        }}
        isFilters={{
          show: true,
          data: [
            {
              name: "filterByStatus",
              value: statusFilter,
              placeholder: "Filter by status",
              options: STATUS_OPTIONS,
              onChange: handleStatusChange,
              className: "w-full sm:!w-44",
            },
          ],
        }}
        isExtraButton={{
          show: true,
          data: [
            {
              title: "Bulk Upload",
              icon: <FiUpload size={16} />,
              onClick: () => setShowBulkUpload(true),
              className:
                "h-10 !bg-transparent !border !border-primary !text-primary hover:!bg-primary hover:!text-white",
            },
            {
              title: "Health Check All",
              icon: <FiRefreshCw size={16} />,
              onClick: () => setShowBulkHealthCheck(true),
              className:
                "h-10 !bg-transparent !border !border-primary !text-primary hover:!bg-primary hover:!text-white",
            },
          ],
        }}
        isListGridView={{
          show: true,
          viewType,
          setViewType,
          viewFor: "staticResidentialIpsViewType",
        }}
        isCreateButton={{
          show: true,
          text: "Add Proxy",
          onclick: () => setShowAddForm(true),
        }}
        isLoading={{
          show: isLoading,
          loaderCmp: (
            <CommonSkeletonLoader
              rows={9}
              type={viewType === "GRID" ? "card" : "table"}
              tableLength={8}
            />
          ),
        }}
        className={!isLoading && isFetching ? "opacity-50" : ""}
      >
        <EnhancedStaticResidentialIpsList
          staticResidentialIps={staticResidentialIps}
          viewType={viewType}
          refetch={refetch}
          onAssignClick={() => setShowAssignForm(true)}
        />

        {totalRecords > pagination.limit && (
          <CommonPagination
            currentPage={pagination.page}
            defaultLimit={pagination.limit}
            totalRecords={totalRecords}
            setCurrentPage={setPagination}
            activePageRecords={staticResidentialIps.length}
            paginationFor="Static Residential Proxies"
            className="mt-4"
          />
        )}
      </CommonCard>

      <EnhancedAddStaticResidentialIp
        open={showAddForm}
        setOpen={setShowAddForm}
        mode="add"
        onSuccess={refetch}
      />

      <EnhancedAddStaticResidentialIp
        open={showAssignForm}
        setOpen={setShowAssignForm}
        mode="assign"
        onSuccess={refetch}
      />

      <EnhancedBulkUploadStaticResidentialIp
        open={showBulkUpload}
        setOpen={setShowBulkUpload}
        onSuccess={refetch}
      />

      <EnhancedBulkHealthCheckStaticResidentialIp
        open={showBulkHealthCheck}
        setOpen={setShowBulkHealthCheck}
        ips={staticResidentialIps}
        onComplete={refetch}
      />
    </>
  );
};

export default EnhancedStaticResidentialIps;
